'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { cx } from '../../../internal/cx.js';
import { cleanPastedHtml, isSafeImageSrc, normalizeLinkInput } from '../../../internal/sanitize.js';
import { buildEditorKit } from './rich-text-editor.extensions.js';
import { useMentionBridge } from './rich-text-editor.mentions.js';
import { DEFAULT_MESSAGES, DEFAULT_TOOLBAR, EditorToolbar, } from './rich-text-editor.toolbar.js';
export { DEFAULT_TOOLBAR as RICH_TEXT_DEFAULT_TOOLBAR, } from './rich-text-editor.toolbar.js';
const NO_HEADINGS = [];
const DEFAULT_HEADINGS = [2, 3];
const asKey = (v) => v === undefined ? undefined : typeof v === 'string' ? v : JSON.stringify(v);
/**
 * A Tiptap rich-text editor themed by Monarch tokens: an accessible
 * formatting toolbar, Markdown shortcuts, safe paste, links, checklists,
 * @mentions, an upload hook and a character limit. Renders a static
 * placeholder on the server; the editor loads after hydration.
 */
export const RichTextEditor = forwardRef(function RichTextEditor({ value, defaultValue, format = 'html', onValueChange, placeholder = '', readOnly = false, disabled = false, invalid = false, autoFocus = false, toolbar = DEFAULT_TOOLBAR, toolbarPosition = 'top', toolbarEnd, headingLevels = DEFAULT_HEADINGS, taskList = true, maxLength, showCharacterCount, onFiles, fileAccept, images, mentions, onEditorKeyDown, extensions, minHeight, maxHeight, name, editorRef, messages: messagesProp, className, style, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, ...rest }, ref) {
    const m = useMemo(() => ({ ...DEFAULT_MESSAGES, ...messagesProp }), [messagesProp]);
    const baseId = `mn-rte-${useId().replace(/:/g, '')}`;
    const countId = `${baseId}-count`;
    const [linkOpen, setLinkOpen] = useState(false);
    const [html, setHtml] = useState('');
    const [characters, setCharacters] = useState(0);
    const fileInput = useRef(null);
    const mention = useMentionBridge(mentions, m);
    // Latest props for the editor's callbacks, which are bound once.
    const hasLinkTool = toolbar !== false && toolbar.includes('link') && !readOnly;
    const live = useRef({ onValueChange, onEditorKeyDown, onFiles, format, mention, hasLinkTool });
    live.current = { onValueChange, onEditorKeyDown, onFiles, format, mention, hasLinkTool };
    const lastEmitted = useRef(asKey(value ?? defaultValue));
    const editorInstance = useRef(null);
    const allowImages = images ?? Boolean(onFiles);
    const levels = headingLevels.length ? headingLevels : NO_HEADINGS;
    const trigger = mentions ? (mentions.trigger ?? '@') : null;
    const kitKey = JSON.stringify([levels, taskList, allowImages, trigger, maxLength]);
    const showCount = showCharacterCount ?? maxLength !== undefined;
    // Read-only content is plain content: no textbox role, so no textbox ARIA
    // (the root becomes a named group instead when a label is given).
    const attributes = {
        role: readOnly ? undefined : 'textbox',
        'aria-multiline': readOnly ? undefined : 'true',
        'aria-label': readOnly ? undefined : ariaLabel,
        'aria-labelledby': readOnly ? undefined : ariaLabelledBy,
        'aria-describedby': readOnly
            ? undefined
            : [ariaDescribedBy, showCount && maxLength ? countId : undefined]
                .filter(Boolean)
                .join(' ') || undefined,
        'aria-disabled': disabled ? 'true' : undefined,
        'aria-invalid': invalid ? 'true' : undefined,
        class: 'mn-rich-text-editor__prose',
        ...(mention?.fieldAttributes ?? {}),
    };
    const placeholderRef = useRef(placeholder);
    placeholderRef.current = placeholder;
    const attrsRef = useRef(attributes);
    attrsRef.current = attributes;
    const insertResults = (results) => {
        const ed = editorInstance.current;
        if (!ed || ed.isDestroyed || !Array.isArray(results))
            return;
        // One insertion, so a just-inserted image is never replaced by the next item.
        const content = [];
        for (const r of results) {
            if (r.type === 'image') {
                if (ed.schema.nodes.image && isSafeImageSrc(r.src))
                    content.push({
                        type: 'image',
                        attrs: { src: r.src, alt: r.alt ?? null, title: r.title ?? null },
                    });
            }
            else {
                const href = normalizeLinkInput(r.href);
                if (href)
                    content.push({ type: 'text', text: r.text, marks: [{ type: 'link', attrs: { href } }] }, { type: 'text', text: ' ' });
            }
        }
        if (content.length)
            ed.chain().focus().insertContent(content).run();
    };
    const handleFiles = (files, source) => {
        const fn = live.current.onFiles;
        if (!fn || files.length === 0)
            return false;
        void Promise.resolve(fn(files, source)).then(insertResults, () => undefined);
        return true;
    };
    const handleFilesRef = useRef(handleFiles);
    handleFilesRef.current = handleFiles;
    const emit = (ed) => {
        const isEmpty = ed.isEmpty;
        const count = ed.storage.characterCount?.characters?.() ?? 0;
        setCharacters(count);
        const htmlOut = isEmpty ? '' : ed.getHTML();
        setHtml(htmlOut);
        const out = live.current.format === 'json' ? ed.getJSON() : htmlOut;
        lastEmitted.current = asKey(out);
        live.current.onValueChange?.(out, { text: ed.getText(), isEmpty, characters: count });
    };
    const editor = useEditor({
        immediatelyRender: false,
        shouldRerenderOnTransaction: true,
        editable: !readOnly && !disabled,
        autofocus: autoFocus ? 'end' : false,
        // On a rebuild (kit options changed) keep what is already written.
        content: editorInstance.current?.getJSON() ?? value ?? defaultValue ?? '',
        extensions: [
            ...buildEditorKit({
                placeholder: () => placeholderRef.current,
                headingLevels: levels,
                maxLength,
                taskList,
                images: allowImages,
                mention: mention
                    ? { trigger: trigger ?? '@', suggestion: mention.suggestion }
                    : undefined,
            }),
            ...(extensions ?? []),
        ],
        editorProps: {
            attributes: () => Object.fromEntries(Object.entries(attrsRef.current).filter(([, v]) => v !== undefined)),
            transformPastedHTML: cleanPastedHtml,
            handleKeyDown: (_view, event) => {
                const ed = editorInstance.current;
                if (!ed || live.current.mention?.isOpen())
                    return false;
                if (live.current.hasLinkTool &&
                    (event.metaKey || event.ctrlKey) &&
                    !event.altKey &&
                    event.key.toLowerCase() === 'k') {
                    event.preventDefault();
                    setLinkOpen(true);
                    return true;
                }
                if (live.current.onEditorKeyDown?.(event, ed) === true) {
                    event.preventDefault();
                    return true;
                }
                return false;
            },
            handlePaste: (_view, event) => {
                const files = Array.from(event.clipboardData?.files ?? []);
                if (handleFilesRef.current(files, 'paste')) {
                    event.preventDefault();
                    return true;
                }
                return false;
            },
            handleDrop: (_view, event, _slice, moved) => {
                if (moved)
                    return false;
                const files = Array.from(event.dataTransfer?.files ?? []);
                if (handleFilesRef.current(files, 'drop')) {
                    event.preventDefault();
                    return true;
                }
                return false;
            },
        },
        onCreate: ({ editor: ed }) => {
            editorInstance.current = ed;
            setCharacters(ed.storage.characterCount?.characters?.() ?? 0);
            setHtml(ed.isEmpty ? '' : ed.getHTML());
        },
        onUpdate: ({ editor: ed }) => emit(ed),
        onDestroy: () => {
            editorInstance.current = null;
        },
    }, [kitKey, extensions]);
    editorInstance.current = editor;
    // Editable follows readOnly / disabled without rebuilding.
    useEffect(() => {
        if (editor && editor.isEditable !== (!readOnly && !disabled))
            editor.setEditable(!readOnly && !disabled, false);
    }, [editor, readOnly, disabled]);
    // ARIA on the editing surface follows props and the suggestion list.
    const attrKey = JSON.stringify([attributes, placeholder]);
    useEffect(() => {
        if (editor && !editor.isDestroyed)
            editor.view.updateState(editor.view.state);
    }, [editor, attrKey]);
    // Controlled value: apply outside changes, ignore our own echoes.
    useEffect(() => {
        if (!editor || value === undefined)
            return;
        const incoming = asKey(value);
        if (incoming === lastEmitted.current)
            return;
        const current = format === 'json'
            ? JSON.stringify(editor.getJSON())
            : editor.isEmpty
                ? ''
                : editor.getHTML();
        lastEmitted.current = incoming;
        if (incoming === current)
            return;
        editor.commands.setContent(value, false);
        setCharacters(editor.storage.characterCount?.characters?.() ?? 0);
        setHtml(editor.isEmpty ? '' : editor.getHTML());
    }, [editor, value, format]);
    useImperativeHandle(editorRef, () => ({
        get editor() {
            return editorInstance.current;
        },
        focus: (position = 'end') => editorInstance.current?.commands.focus(position),
        clear: () => {
            editorInstance.current?.commands.clearContent(true);
        },
        getHTML: () => {
            const ed = editorInstance.current;
            return !ed || ed.isEmpty ? '' : ed.getHTML();
        },
        getJSON: () => editorInstance.current?.getJSON() ?? null,
        getText: () => editorInstance.current?.getText() ?? '',
        isEmpty: () => editorInstance.current?.isEmpty ?? true,
        setContent: (v) => {
            const ed = editorInstance.current;
            if (!ed)
                return;
            ed.commands.setContent(v, false);
            lastEmitted.current = asKey(v);
            setCharacters(ed.storage.characterCount?.characters?.() ?? 0);
            setHtml(ed.isEmpty ? '' : ed.getHTML());
        },
        insertText: (text) => {
            editorInstance.current?.chain().focus().insertContent({ type: 'text', text }).run();
        },
        insertContent: (content) => {
            editorInstance.current?.chain().focus().insertContent(content).run();
        },
    }), []);
    const editable = !readOnly;
    const items = toolbar === false || !editable ? null : toolbar;
    const left = maxLength !== undefined ? maxLength - characters : null;
    const near = left !== null && maxLength !== undefined && left <= Math.max(10, maxLength * 0.1);
    const toolbarRow = items || toolbarEnd ? (_jsxs("div", { className: "mn-rich-text-editor__bar", "data-position": toolbarPosition, children: [items && editor ? (_jsx(EditorToolbar, { editor: editor, items: items, headingLevels: levels, messages: m, disabled: disabled, linkOpen: linkOpen, onLinkOpenChange: setLinkOpen, pickFile: onFiles && !disabled ? () => fileInput.current?.click() : undefined })) : (_jsx("div", { className: "mn-rich-text-editor__toolbar", "aria-hidden": true, "data-placeholder": "" })), toolbarEnd ? _jsx("div", { className: "mn-rich-text-editor__bar-end", children: toolbarEnd }) : null] })) : null;
    return (_jsxs("div", { ref: ref, ...rest, ...(readOnly && (ariaLabel || ariaLabelledBy)
            ? { role: 'group', 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy }
            : {}), className: cx('mn-rich-text-editor', className), style: {
            ...(minHeight !== undefined
                ? {
                    '--mn-rich-text-editor-min-height': typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
                }
                : {}),
            ...(maxHeight !== undefined
                ? {
                    '--mn-rich-text-editor-max-height': typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
                }
                : {}),
            ...style,
        }, "data-readonly": readOnly || undefined, "data-disabled": disabled || undefined, "data-invalid": invalid || undefined, "data-loading": editor ? undefined : '', children: [toolbarPosition === 'top' ? toolbarRow : null, _jsx("div", { className: "mn-rich-text-editor__content", onMouseDown: (e) => {
                    // Clicking the padding below the text puts the caret at the end.
                    if (e.target === e.currentTarget && editor && editable && !disabled) {
                        e.preventDefault();
                        editor.commands.focus('end');
                    }
                }, children: editor ? (_jsx(EditorContent, { editor: editor })) : (_jsxs("div", { className: "mn-rich-text-editor__prose mn-rich-text-editor__static", "aria-busy": "true", children: [_jsx("p", { "data-empty": placeholder ? '' : undefined, children: placeholder || ' ' }), _jsx("span", { className: "mn-visually-hidden", children: m.loading })] })) }), toolbarPosition === 'bottom' ? toolbarRow : null, showCount && editable ? (_jsxs("div", { className: "mn-rich-text-editor__footer", children: [_jsx("span", { id: countId, className: "mn-rich-text-editor__count", "data-state": left !== null && left < 0 ? 'over' : near ? 'near' : undefined, children: m.count(characters, maxLength) }), _jsx("span", { className: "mn-visually-hidden", role: "status", children: near && left !== null ? m.remaining(left) : '' })] })) : null, onFiles ? (_jsx("input", { ref: fileInput, type: "file", multiple: true, hidden: true, tabIndex: -1, ...(fileAccept ? { accept: fileAccept } : {}), onChange: (e) => {
                    const files = Array.from(e.target.files ?? []);
                    e.target.value = '';
                    handleFiles(files, 'picker');
                } })) : null, name ? _jsx("input", { type: "hidden", name: name, value: html }) : null, mention?.list] }));
});
//# sourceMappingURL=rich-text-editor.js.map